import React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import { GridColumnType } from "components/grid";
import Flex from "components/layout/Flex";
import { BadgeKind } from "constants/settings/interfaces";
import { trackEngagement } from "helpers/analytics";
import useNewFeaturesInformation from "hooks/settings/use-new-features-information";
import { SettingsViewActions } from "store/views/settings";

import { Badge, NewFeatureBadge } from "./Badges";
import { isExternalNavigationItem } from "./helpers";
import { ISettingsSideNavigationItemWithBadge } from "./interfaces";
import { navigationItemButtonStyles } from "./styles";

interface INavigationItemProps {
  item: ISettingsSideNavigationItemWithBadge;
  active?: boolean;
  badgeKind?: BadgeKind;
}

export const NavigationItem: React.FC<INavigationItemProps> = ({ item, active = false }) => {
  const hasNewFeature = useNewFeaturesInformation(item.section);
  const dispatch = useDispatch();
  const isExternal = isExternalNavigationItem(item);
  const onSectionVisited = (): void => {
    dispatch(SettingsViewActions.setFocusedColumn({ focusedColumn: GridColumnType.Second }));
  };

  const props = {
    "data-active": active,
    "data-testid": `navigation-item-${item.name.toLowerCase().replace(" ", "-")}`,
    css: navigationItemButtonStyles,
  };

  const Children = (
    <>
      <Flex gap="4px" alignItems="center">
        {item.name}
        {hasNewFeature && <NewFeatureBadge />}
      </Flex>
      <Badge kind={item.badgeKind} />
    </>
  );

  return isExternal ? (
    <a
      href={item.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
      onClick={() => trackEngagement(`Settings: ${item.name} visited`)}
    >
      {Children}
    </a>
  ) : (
    <Link type="button" {...props} to={item.path} onClick={onSectionVisited}>
      {Children}
    </Link>
  );
};
